// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CloseRounded as CloseIcon } from '@/constants/hardfin-standard-icons';
import {
  Box,
  ClickAwayListener,
  Container,
  Divider,
  Fade,
  IconButton,
  Paper,
  Popper,
  PopperProps,
  Stack,
  Typography,
  styled,
} from '@mui/material';
import { PropsWithChildren, useState } from 'react';

// The styled components Arrow, PopperContent, and PopperContainer contain
// some "magic" values to position and size the tooltip's arrow correctly.

const Arrow = styled('span')(({ theme }) => ({
  overflow: 'hidden',
  position: 'absolute',
  width: '1em',
  height: '0.71em',
  boxSizing: 'border-box',
  color: theme.palette.grey[100],
  '&::before': {
    content: '""',
    margin: 'auto',
    display: 'block',
    width: '100%',
    height: '100%',
    backgroundColor: 'currentColor',
    transform: 'rotate(45deg)',
  },
}));

const PopperContent = styled(Paper)(({ theme }) => ({
  boxShadow: theme.shadows[8],
  backgroundColor: theme.palette.grey[100],
  '.popper[data-popper-placement*="left"] &': {
    transformOrigin: 'right center',
  },
  '.popper[data-popper-placement*="right"] &': {
    transformOrigin: 'left center',
  },
  '.popper[data-popper-placement*="top"] &': {
    transformOrigin: 'center bottom',
    marginBottom: '14px',
  },
  '.popper[data-popper-placement*="bottom"] &': {
    transformOrigin: 'center top',
    marginTop: '14px',
  },
}));

const PopperContainer = styled(Popper)(({ theme }) => ({
  zIndex: theme.zIndex.tooltip,
  '&[data-popper-placement*="bottom"] .arrow': {
    top: 0,
    marginTop: '-0.71em',
    '&::before': {
      transformOrigin: '0 100%',
    },
  },
  '&[data-popper-placement*="top"] .arrow': {
    bottom: 0,
    marginBottom: '-0.71em',
    '&::before': {
      transformOrigin: '100% 0',
    },
  },
  '&[data-popper-placement*="right"] .arrow': {
    height: '1em',
    width: '0.71em',
    '&::before': {
      transformOrigin: '100% 100%',
    },
  },
  '&[data-popper-placement*="left"] .arrow': {
    height: '1em',
    width: '0.71em',
    '&::before': {
      transformOrigin: '0 0',
    },
  },
}));

interface Props extends Pick<PopperProps, 'placement'> {
  /**
   * If true, the tooltip is visible.
   */
  isOpen: boolean;

  /**
   * The title of the tooltip, displayed above the content.
   */
  title: string;

  /**
   * An element to which to attach the tooltip.
   */
  anchorElement: Element | null;

  /**
   * A callback fired when the tooltip requests to be closed.
   */
  onClose: () => void;
}

/**
 * A controlled tooltip component that displays its content below a title and
 * a close button. This component is intended to be used to display educational
 * information to users.
 */
export const EducationalTooltip = (props: PropsWithChildren<Props>) => {
  // Unfortunately MUI's internal Popper component doesn't position the arrow
  // element correctly if it is stored in a ref. Instead, it is stored in a
  // state hook so that the component is updated when the arrow element changes.
  const [arrowRef, setArrowRef] = useState<HTMLSpanElement | null>(null);

  const modifiers: PopperProps['modifiers'] = [
    { name: 'arrow', options: { element: arrowRef, padding: 8 } },
    { name: 'offset', options: { offset: [0, 12] } },
  ];

  return (
    <PopperContainer
      transition
      disablePortal
      open={props.isOpen}
      anchorEl={props.anchorElement}
      placement={props.placement}
      modifiers={modifiers}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps}>
          <PopperContent className="popper">
            <Arrow className="arrow" ref={setArrowRef} />
            <ClickAwayListener onClickAway={props.onClose}>
              <Container disableGutters maxWidth="xs">
                <Stack
                  useFlexGap
                  spacing={1}
                  paddingX={2}
                  paddingY={1}
                  direction="row"
                  alignItems="center"
                >
                  <Typography variant="overline" marginRight="auto">
                    {props.title}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label="close"
                    onClick={props.onClose}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Divider />
                <Box padding={2}>{props.children}</Box>
              </Container>
            </ClickAwayListener>
          </PopperContent>
        </Fade>
      )}
    </PopperContainer>
  );
};
